// Add a worker: the state, without any of the chrome.
//
// One entry point for every way a worker joins the pool. There used to be a
// "Register existing" button, a "Deploy worker" button and a disabled remote
// option living inside the deploy wizard — three doors onto the same question,
// which meant an operator had to know the answer before choosing a door.
//
// Single form instance + per-step validation: see useWizard for why Next must not
// validate the whole schema. The register branch runs its own form, because it
// collects the load-balancer fields the edit dialog already owns rather than a
// second copy of them.
//
// This is a hook rather than a component because it has two callers with
// incompatible chrome. /workers/new wraps it in a WizardShell; the setup flow
// embeds it in step 4, and setup is itself already inside a WizardShell. A
// `layout` prop would have made one of those a shell inside a shell. Returning
// the step body and letting each caller draw its own frame keeps the state
// shared and the chrome separate.

import { zodResolver } from "@hookform/resolvers/zod";
import { type FleetHostCreateInput, FleetHostCreateSchema } from "@schemas";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { toast } from "sonner";
import type { UseWizardResult } from "@/components/wizard/use-wizard";
import { useWizard, type WizardStepDef } from "@/components/wizard/use-wizard";
import {
	WizardSummaryList,
	WizardSummaryPending,
	WizardSummaryValue,
} from "@/components/wizard/wizard-summary";
import {
	NEW_WORKER_DEFAULTS,
	toWorkerPayload,
	WorkerConfigFields,
} from "@/components/workers/worker-config-fields";
import { api, unwrap } from "@/lib/api";
import {
	SSH_KEYS_QUERY_KEY,
	type SshKeyMetadata,
	useSshKeys,
} from "@/lib/ssh-keys";
import {
	DEPLOY_WORKER_DEFAULTS,
	type DeployTarget,
	DeployWorkerSchema,
	type DeployWorkerValue,
	NEW_SSH_KEY,
	STEP_FIELDS,
	toSshRequest,
} from "./schema";
import { ConfigureStep } from "./steps/configure-step";
import { ConnectionStep } from "./steps/connection-step";
import { ReviewStep } from "./steps/review-step";
import { TargetStep } from "./steps/target-step";
import { useWorkerImages, type WorkerImageVersions } from "./use-worker-images";

export interface AddWorkerStepMeta {
	title: string;
	blurb: string;
	/** "Why this step matters", for a shell with a context rail. */
	why: string;
}

const STEP_TITLES: Record<string, AddWorkerStepMeta> = {
	target: {
		title: "Where should this worker come from?",
		blurb:
			"Workers launch and hold the browsers. This decides whose CPU they use.",
		why: "A worker on this machine competes with the manager for CPU and memory. A dedicated host does not.",
	},
	configure: {
		title: "Name and version",
		blurb:
			"The name identifies the worker everywhere — logs, routing and its container.",
		why: "The name becomes the container name too, so it is checked against the fleet before anything is created.",
	},
	connection: {
		title: "How do we get onto that machine?",
		blurb:
			"SSH, once, to install and start the worker. The key is not stored unless you ask.",
		why: "Test connection reads the machine before anything is written, so a missing Docker is a two-second answer rather than a failed deploy.",
	},
	register: {
		title: "How do we reach it?",
		blurb:
			"The load balancer needs an address and a key. Nothing is created here.",
		why: "This records a worker you already run. Nothing is installed, started or changed on that machine.",
	},
	review: {
		title: "Review",
		blurb: "Nothing has been created yet.",
		why: "The last point at which changing your mind costs nothing. Every row here has a way back to the field behind it.",
	},
};

const TARGET_LABELS: Record<DeployTarget, string> = {
	local: "This machine",
	remote: "Another machine",
	register: "Already running",
};

export interface AddWorkerWizardOptions {
	/** Called with the operation id once a deploy starts. */
	onStarted: (operationId: string) => void;
	/** Called once an already-running worker has been registered. */
	onRegistered?: (hostname: string) => void;
	/** Preselects the target card, e.g. from `/workers/new?target=remote`. */
	initialTarget?: DeployTarget;
}

export interface AddWorkerWizardState {
	form: UseFormReturn<DeployWorkerValue>;
	wizard: UseWizardResult<DeployWorkerValue>;
	/** Heading + blurb for the step in front of the operator. */
	meta: AddWorkerStepMeta;
	/** The active step's body, with no surrounding transition or heading. */
	stepNode: ReactNode;
	submit: (event: React.FormEvent<HTMLFormElement>) => void;
	/** Terminal action's label, including its in-flight wording. */
	submitLabel: string;
	pending: boolean;
	/** True on the "already running" branch, which registers rather than deploys. */
	registering: boolean;
	/** Anything typed that a navigation away would lose. */
	dirty: boolean;
	/** What has been chosen so far, for a wizard rail. */
	summary: ReactNode;
}

export function useAddWorkerWizard({
	onStarted,
	onRegistered,
	initialTarget,
}: AddWorkerWizardOptions): AddWorkerWizardState {
	const queryClient = useQueryClient();
	const images = useWorkerImages();
	// Fetched here rather than only in the connection step so the rail and the
	// review row can name the chosen key. The list is cached, so this is the same
	// request the picker makes.
	const sshKeys = useSshKeys();

	const form = useForm<DeployWorkerValue>({
		resolver: zodResolver(DeployWorkerSchema),
		mode: "onTouched",
		defaultValues: initialTarget
			? { ...DEPLOY_WORKER_DEFAULTS, target: initialTarget }
			: DEPLOY_WORKER_DEFAULTS,
	});
	const target = form.watch("target");
	const registering = target === "register";
	const remote = target === "remote";

	// A second, independent form: registering asks for the whole load-balancer
	// record, and folding those fields into the deploy schema would make `name`
	// and `imageTag` required on a path that uses neither.
	const registerForm = useForm<FleetHostCreateInput>({
		resolver: zodResolver(FleetHostCreateSchema),
		mode: "onTouched",
		defaultValues: NEW_WORKER_DEFAULTS,
	});

	const steps: WizardStepDef<DeployWorkerValue>[] = [
		{ id: "target", label: "Target", fields: [...STEP_FIELDS.target] },
		{
			id: "configure",
			label: "Configure",
			fields: [...STEP_FIELDS.configure],
			skip: registering,
		},
		{
			id: "connection",
			label: "Connection",
			fields: [...STEP_FIELDS.connection],
			skip: !remote,
		},
		{
			id: "register",
			label: "Connection",
			fields: [...STEP_FIELDS.register],
			skip: !registering,
		},
		{ id: "review", label: "Review", fields: [], skip: registering },
	];
	const wizard = useWizard(form, steps);

	const deploy = useMutation({
		mutationFn: async (values: DeployWorkerValue) =>
			(await unwrap(
				values.target === "remote"
					? api.api.workers.provision.remote.post({
							...toSshRequest(values),
							hostname: values.name,
							imageTag: values.imageTag,
							installDocker: values.installDocker,
							restrictFirewall: values.restrictFirewall,
							autoUpdate: values.autoUpdate,
						})
					: api.api.workers.provision.local.post({
							hostname: values.name,
							imageTag: values.imageTag,
						}),
			)) as { operationId: string },
		onSuccess: ({ operationId }, values) => {
			// A deploy that was asked to save its key has just added one, and the
			// picker on the next visit reads that list.
			if (values.saveKey) {
				queryClient.invalidateQueries({ queryKey: SSH_KEYS_QUERY_KEY });
			}
			onStarted(operationId);
		},
		onError: (err) => {
			// A name taken between the availability check and submit lands here;
			// send the operator back to the field that needs changing.
			const message = err instanceof Error ? err.message : "Deploy failed";
			if (/already exists/i.test(message)) {
				form.setError("name", { type: "server", message });
				wizard.goTo("configure");
				return;
			}
			// Everything else a remote deploy is refused for — no release origin, no
			// org key, no ssh binary — is about this cluster rather than a field, so
			// it belongs in a toast the operator can read and act on elsewhere.
			toast.error(message);
		},
	});

	const register = useMutation({
		mutationFn: (values: FleetHostCreateInput) =>
			unwrap(api.api.workers.post(toWorkerPayload(values))),
		onSuccess: (_data, values) => {
			toast.success(`Registered ${values.hostname}`);
			queryClient.invalidateQueries({ queryKey: ["cluster"] });
			queryClient.invalidateQueries({ queryKey: ["workers"] });
			onRegistered?.(values.hostname);
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Register failed"),
	});

	const pending = deploy.isPending || register.isPending;

	const submit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (registering) {
			void registerForm.handleSubmit((values) => register.mutate(values))();
			return;
		}
		void form.handleSubmit((values) => deploy.mutate(values))();
	};

	const stepId = wizard.current.id;
	const stepNode = (
		<>
			{stepId === "target" ? <TargetStep form={form} /> : null}
			{stepId === "configure" ? (
				<ConfigureStep form={form} images={images.data} />
			) : null}
			{stepId === "connection" ? <ConnectionStep form={form} /> : null}
			{stepId === "register" ? (
				<WorkerConfigFields form={registerForm} />
			) : null}
			{stepId === "review" ? (
				<ReviewStep
					values={form.getValues()}
					images={images.data}
					keyLabel={describeKeyChoice(form.getValues(), sshKeys.data)}
					onEdit={wizard.goTo}
				/>
			) : null}
		</>
	);

	const submitLabel = registering
		? register.isPending
			? "Registering…"
			: "Register worker"
		: deploy.isPending
			? "Deploying…"
			: "Deploy worker";

	return {
		form,
		wizard,
		meta: STEP_TITLES[stepId] ?? { title: "Add a worker", blurb: "", why: "" },
		stepNode,
		submit,
		submitLabel,
		pending,
		registering,
		dirty: form.formState.isDirty || registerForm.formState.isDirty,
		summary: (
			<ChoicesSoFar
				target={target}
				name={form.watch("name")}
				registerHostname={registerForm.watch("hostname")}
				imageTag={form.watch("imageTag")}
				sshUser={form.watch("sshUser")}
				sshHost={form.watch("sshHost")}
				images={images.data}
			/>
		),
	};
}

/**
 * Which key this deploy will use, in one phrase.
 *
 * Worth its own row on review because it is the difference between a worker that
 * can be redeployed in a click and one that cannot, and that consequence is
 * decided two steps earlier by a checkbox.
 */
function describeKeyChoice(
	values: DeployWorkerValue,
	keys: SshKeyMetadata[] | undefined,
): string {
	if (values.sshKeyId !== NEW_SSH_KEY) {
		const saved = keys?.find((k) => k.id === values.sshKeyId);
		return saved ? `${saved.name} (saved)` : "a saved key";
	}
	if (values.saveKey && values.keyName.length > 0) {
		return `pasted, saving as "${values.keyName}"`;
	}
	return "pasted, not saved";
}

/**
 * The rail summary: what the operator has settled on, in the order they settle
 * on it. Everything not yet chosen renders as the shared em dash, so the rail
 * reads as a form filling in rather than as facts about the cluster.
 */
function ChoicesSoFar({
	target,
	name,
	registerHostname,
	imageTag,
	sshUser,
	sshHost,
	images,
}: {
	target: DeployTarget;
	name: string;
	registerHostname: string | undefined;
	imageTag: string;
	sshUser: string;
	sshHost: string;
	images: WorkerImageVersions | undefined;
}) {
	const registering = target === "register";
	// Registering records a worker that already exists, so its name comes from
	// the load-balancer form and there is no image tag to report.
	const workerName = registering ? (registerHostname ?? "") : name;
	const version =
		imageTag === "latest" && images?.latest
			? `latest (${images.latest})`
			: imageTag;

	const rows = [
		{
			label: "Runs on",
			value: <span className="text-foreground">{TARGET_LABELS[target]}</span>,
		},
		{
			label: "Name",
			value: workerName ? (
				<WizardSummaryValue>{workerName}</WizardSummaryValue>
			) : (
				<WizardSummaryPending />
			),
		},
	];

	if (!registering) {
		rows.push({
			label: "Version",
			value: <WizardSummaryValue>{version}</WizardSummaryValue>,
		});
	}

	if (target === "remote") {
		const address = sshHost.trim();
		rows.push({
			label: "Machine",
			value: address ? (
				<WizardSummaryValue>
					{sshUser.trim() ? `${sshUser.trim()}@${address}` : address}
				</WizardSummaryValue>
			) : (
				<WizardSummaryPending />
			),
		});
	}

	return <WizardSummaryList heading="This worker" rows={rows} />;
}
